import * as React from "react";
import {
  composeRenderProps,
  type DisclosureRenderProps,
  Button as RACButton,
  type ButtonProps as RACButtonProps,
  Disclosure as RACDisclosure,
  DisclosureGroup as RACDisclosureGroup,
  type DisclosureGroupProps as RACDisclosureGroupProps,
  DisclosurePanel as RACDisclosurePanel,
  type DisclosurePanelProps as RACDisclosurePanelProps,
  type DisclosureProps as RACDisclosureProps,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { Icon } from "@/components/Icon";
import { composeTailwindRenderProps, focusRing } from "@/utils";
import { ChartLabelledBy } from "../charts/ChartHeader/ChartHeader";

type DisclosureStateProps = DisclosureRenderProps;

export type CollapseVariant = "contained" | "bordered" | "plain";
export type CollapseDensity = "comfortable" | "compact";

type CollapseContextValue = {
  variant: CollapseVariant;
  density: CollapseDensity;
  showDividers: boolean;
};

const CollapseContext = React.createContext<CollapseContextValue>({
  variant: "contained",
  density: "comfortable",
  showDividers: true,
});

const CollapseItemContext = React.createContext<DisclosureStateProps | null>(null);

const containerVariantStyles: Record<CollapseVariant, string> = {
  contained: "rounded-lg border border-border-default bg-background-surface overflow-hidden",
  bordered: "rounded-lg border border-border-default bg-background-surface",
  plain: "bg-transparent",
};

const containerDividerStyles = {
  true: "divide-y divide-border-default",
  false: "gap-3",
} as const;

const headerPaddingStyles: Record<CollapseDensity, string> = {
  comfortable: "px-5 py-3",
  compact: "px-4 py-2.5",
};

const panelPaddingStyles: Record<CollapseDensity, string> = {
  comfortable: "px-5 pt-0",
  compact: "px-4 pt-0",
};

const indicatorWrapperStyles: Record<CollapseDensity, string> = {
  comfortable: "h-4 w-4",
  compact: "h-3.5 w-3.5",
};

function useCollapseContext() {
  return React.useContext(CollapseContext);
}

function useCollapseItemContext(componentName: string) {
  const context = React.useContext(CollapseItemContext);

  if (!context) {
    throw new Error(`${componentName} must be used within a <CollapseItem>`);
  }

  return context;
}

function getCollapseItemStyles({
  variant,
  showDividers,
  className,
}: {
  variant: CollapseVariant;
  showDividers: boolean;
  className?: string;
}) {
  const baseStyles = "group/collapse-item w-full transition-colors";
  const variantStyles: Record<CollapseVariant, string> = {
    contained: "bg-background-surface",
    bordered: "bg-background-surface",
    plain: "bg-transparent",
  };

  const dividerStyles = showDividers
    ? ""
    : "rounded-lg border border-border-default bg-background-surface overflow-hidden";

  return twMerge(baseStyles, variantStyles[variant], dividerStyles, className);
}

function getHeaderButtonStyles({
  isDisabled,
  isPressed,
  density,
  variant,
  showDividers,
  className,
}: {
  isDisabled: boolean;
  isPressed: boolean;
  density: CollapseDensity;
  variant: CollapseVariant;
  showDividers: boolean;
  className?: string;
}) {
  const baseStyles = "group/collapse-header flex w-full items-start gap-4 text-left transition-colors duration-150";
  const interactiveStyles = isDisabled ? "cursor-not-allowed text-text-disabled" : "cursor-pointer text-text-primary";
  const pressedStyles = isPressed && !isDisabled ? "bg-background-hover" : "";
  const densityStyles = headerPaddingStyles[density];

  return twMerge(
    baseStyles,
    focusRing(),
    densityStyles,
    interactiveStyles,
    pressedStyles,
    variant === "plain" && showDividers ? "px-0" : "",
    className
  );
}

function getPanelStyles({
  density,
  variant,
  showDividers,
  className,
}: {
  density: CollapseDensity;
  variant: CollapseVariant;
  showDividers: boolean;
  className?: string;
}) {
  const baseStyles = "text-text-primary";
  const paddingStyles = panelPaddingStyles[density];
  const radiusStyles = showDividers ? "" : "rounded-b-lg";

  return twMerge(
    baseStyles,
    paddingStyles,
    radiusStyles,
    // Mirrors the header's carve-out below. A plain collapse with dividers draws
    // no surface of its own, so its header sits flush at `px-0` and lets the
    // surrounding layout own the side inset — the panel has to do the same, or
    // every body renders 16px inboard of the heading it belongs to.
    variant === "plain" && showDividers ? "px-0" : "",
    className
  );
}

export interface CollapseProps extends Omit<RACDisclosureGroupProps, "className"> {
  variant?: CollapseVariant;
  density?: CollapseDensity;
  showDividers?: boolean;
  className?: RACDisclosureGroupProps["className"];
}

/**
 * Collapse
 *
 * A composable disclosure group built on `react-aria-components` that follows the
 * Edges design system. Supports contained, bordered, and plain variants along
 * with compact or comfortable density options.
 */
export function Collapse({
  variant = "contained",
  density = "comfortable",
  showDividers = true,
  className,
  ...props
}: CollapseProps) {
  const contextValue = React.useMemo<CollapseContextValue>(
    () => ({ variant, density, showDividers }),
    [variant, density, showDividers]
  );

  const baseContainerStyles = twMerge(
    "flex w-full flex-col",
    containerVariantStyles[variant],
    showDividers ? containerDividerStyles.true : containerDividerStyles.false,
    variant === "plain" && showDividers ? "border-y border-border-default/60" : ""
  );

  return (
    <CollapseContext.Provider value={contextValue}>
      <RACDisclosureGroup {...props} className={composeTailwindRenderProps(className, baseContainerStyles)} />
    </CollapseContext.Provider>
  );
}

export interface CollapseItemProps extends Omit<RACDisclosureProps, "className" | "children"> {
  className?: RACDisclosureProps["className"];
  children?: React.ReactNode;
}

export function CollapseItem({ className, children, ...props }: CollapseItemProps) {
  const { variant, showDividers } = useCollapseContext();

  return (
    <RACDisclosure
      {...props}
      className={composeRenderProps(className, (resolvedClassName) =>
        getCollapseItemStyles({
          variant,
          showDividers,
          className: resolvedClassName,
        })
      )}
    >
      {(renderProps) => <CollapseItemContext.Provider value={renderProps}>{children}</CollapseItemContext.Provider>}
    </RACDisclosure>
  );
}

CollapseItem.displayName = "CollapseItem";

export interface CollapseHeaderProps extends Omit<RACButtonProps, "children" | "slot"> {
  title?: React.ReactNode;
  description?: React.ReactNode;
  startContent?: React.ReactNode;
  endContent?: React.ReactNode;
  indicator?: React.ReactNode | ((state: { isExpanded: boolean; isDisabled: boolean }) => React.ReactNode);
  indicatorPosition?: "start" | "end";
  showIndicator?: boolean;
  keepIndicatorSpace?: boolean;
  children?: React.ReactNode;
}

export function CollapseHeader({
  title,
  description,
  startContent,
  endContent,
  indicator,
  indicatorPosition = "end",
  showIndicator = true,
  keepIndicatorSpace = false,
  children,
  className,
  ...props
}: CollapseHeaderProps) {
  const disclosure = useCollapseItemContext("CollapseHeader");
  const { density, variant, showDividers } = useCollapseContext();

  const indicatorWrapperClass = indicatorWrapperStyles[density];

  return (
    <RACButton
      {...props}
      slot="trigger"
      data-expanded={disclosure.isExpanded ? "true" : undefined}
      className={composeRenderProps(className, (resolvedClassName, buttonRenderProps) =>
        getHeaderButtonStyles({
          isDisabled: buttonRenderProps.isDisabled ?? false,
          isPressed: buttonRenderProps.isPressed ?? false,
          density,
          variant,
          showDividers,
          className: resolvedClassName,
        })
      )}
    >
      {(renderProps) => {
        const isDisabled = renderProps.isDisabled ?? false;
        const indicatorContent = showIndicator
          ? typeof indicator === "function"
            ? indicator({ isExpanded: disclosure.isExpanded, isDisabled })
            : (indicator ?? (
                <Icon
                  name="CaretDown"
                  size={density === "compact" ? "xs" : "sm"}
                  weight="bold"
                  aria-hidden="true"
                  className={twMerge(
                    "text-text-secondary transition-transform duration-200",
                    disclosure.isExpanded ? "rotate-180" : "rotate-0"
                  )}
                />
              ))
          : null;

        const indicatorNode = indicatorContent ? (
          <span
            className={twMerge(
              "flex flex-shrink-0 items-center justify-center text-text-secondary",
              indicatorWrapperClass
            )}
            aria-hidden="true"
          >
            {indicatorContent}
          </span>
        ) : keepIndicatorSpace ? (
          <span
            className={twMerge("flex flex-shrink-0 items-center justify-center", indicatorWrapperClass)}
            aria-hidden="true"
          />
        ) : null;

        const content = children ?? (
          <div className="flex min-w-0 flex-1 flex-col gap-1 text-left">
            {title && <span className="truncate font-medium text-text-primary">{title}</span>}
            {description && <span className="text-sm text-text-secondary">{description}</span>}
          </div>
        );

        return (
          <div className="flex w-full items-center gap-4">
            {indicatorPosition === "start" && indicatorNode}
            {startContent && <div className="flex flex-shrink-0 items-center text-text-secondary">{startContent}</div>}
            <div className="flex min-w-0 flex-1 items-center gap-3">
              {content}
              {endContent && (
                <div className="flex flex-shrink-0 items-center text-sm text-text-secondary">{endContent}</div>
              )}
            </div>
            {indicatorPosition === "end" && indicatorNode}
          </div>
        );
      }}
    </RACButton>
  );
}

CollapseHeader.displayName = "CollapseHeader";

export interface CollapseContentProps extends Omit<RACDisclosurePanelProps, "className"> {
  className?: RACDisclosurePanelProps["className"];
}

export function CollapseContent({ className, children, ...props }: CollapseContentProps) {
  const { density, variant, showDividers } = useCollapseContext();

  return (
    <RACDisclosurePanel
      {...props}
      className={composeRenderProps(className, (resolvedClassName) =>
        getPanelStyles({
          density,
          variant,
          showDividers,
          className: resolvedClassName,
        })
      )}
    >
      {/* A disclosure always has a visible header: it is the thing you click, and
          the only thing on screen while collapsed. Charts inside one are
          therefore always already labelled — unconditionally, unlike Section,
          which can legitimately have no title. */}
      <ChartLabelledBy>{children}</ChartLabelledBy>
    </RACDisclosurePanel>
  );
}

CollapseContent.displayName = "CollapseContent";
